import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { getGeoLocation, submitLead, redirectToApplication } from '../services/api';
import { Button } from './ui/button';
import './LeadModal.css';

const LeadModal = ({ isOpen, onClose, buttonText = '' }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [country, setCountry] = useState('');
  const [countryCode, setCountryCode] = useState('us');
  const [phone, setPhone] = useState('');
  const [dialCode, setDialCode] = useState('1');

  useEffect(() => {
    // Fetch geolocation when modal opens
    const fetchGeoData = async () => {
      const geoData = await getGeoLocation();
      if (geoData && geoData.data) {
        setCountry(geoData.data.countryName || '');
        const code = (geoData.data.countryCode || 'US').toLowerCase();
        setCountryCode(code);
      }
    };
    if (isOpen) {
      fetchGeoData();
    }
  }, [isOpen]);

  const handlePhoneChange = (value, country) => {
    setPhone(value);
    setDialCode(country.dialCode);
    setCountryCode(country.countryCode);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const formData = new FormData(e.target);
    
    // Extract only numeric phone number (remove country code)
    const phoneNumber = phone.replace(/\D/g, '').replace(dialCode, '');

    const leadData = {
      name: formData.get('name'),
      email: formData.get('email'),
      phoneNumber: phoneNumber,
      countryDialCode: parseInt(dialCode),
      country: countryCode.toUpperCase(),
      description: formData.get('description'),
      packageTitle: buttonText || 'General Inquiry',
      packageAmount: 0
    };

    const result = await submitLead(leadData);

    if (result.success) {
      // Redirect to application page
      redirectToApplication(result.customerId);
    } else {
      setError(result.error);
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          <X size={24} />
        </button>

        <div className="modal-grid">
          {/* Left Side - Image */}
          <div className="modal-image-section">
            <div className="modal-image-overlay">
              <h2 className="modal-image-title">Get Started with Unity Trademark</h2>
              <p className="modal-image-subtitle">
                Complete the form and our experts will contact you within 24 hours
              </p>
              <div className="modal-features">
                <div className="modal-feature">
                  <span className="feature-check">✓</span>
                  <span>Expert Consultation</span>
                </div>
                <div className="modal-feature">
                  <span className="feature-check">✓</span>
                  <span>Fast Processing</span>
                </div>
                <div className="modal-feature">
                  <span className="feature-check">✓</span>
                  <span>Professional Service</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="modal-form-section">
            <div className="modal-form-header">
              <h3 className="modal-form-title">Request a Consultation</h3>
              <p className="modal-form-subtitle">Fill out the details below</p>
            </div>

            {error && (
              <div className="modal-error">
                <span className="error-icon">⚠️</span>
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="modal-form">
              <div className="modal-form-group">
                <label className="modal-form-label">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  placeholder="Enter your full name"
                  required
                  className="modal-form-input"
                  disabled={loading}
                />
              </div>

              <div className="modal-form-group">
                <label className="modal-form-label">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  placeholder="your.email@example.com"
                  required
                  className="modal-form-input"
                  disabled={loading}
                  pattern="[^\s@]+@[^\s@]+\.[^\s@]+"
                  title="Please enter a valid email address"
                />
              </div>

              <div className="modal-form-group">
                <label className="modal-form-label">Contact Number *</label>
                <PhoneInput
                  country={countryCode}
                  value={phone}
                  onChange={handlePhoneChange}
                  disabled={loading}
                  inputProps={{
                    name: 'phone',
                    required: true,
                    placeholder: 'Enter phone number'
                  }}
                  containerClass="modal-phone-input-container"
                  inputClass="modal-phone-input-field"
                  buttonClass="modal-phone-input-button"
                  dropdownClass="modal-phone-input-dropdown"
                />
              </div>

              <div className="modal-form-group">
                <label className="modal-form-label">Description *</label>
                <textarea
                  name="description"
                  placeholder="Tell us about your needs..."
                  required
                  className="modal-form-textarea"
                  disabled={loading}
                  rows="2"
                />
              </div>

              <Button 
                type="submit" 
                size="lg" 
                className="modal-submit-btn" 
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <span>Submit Request</span>
                    <span className="arrow-icon">→</span>
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadModal;
